import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import { DateFilterSelect, monthsOf, type DateFilterId, DEFAULT_DATE_FILTER } from './IcmDateFilter';
import { openPoReportInNewTab, PO_REPORT_TAB_BLOCKED } from './poReportTab';

/** One row of the MAIN view — a vendor with the requisition lines still waiting
 *  to be ordered (legacy Requisition_ListView_Purchaseorder_Mainview). */
interface VendorGroupRow {
  vendorId: string | null;
  vendorName: string;
  numItem: number;
  totalPrice: number;
}

/** One row of the HISTORY view — an ordered requisition line carrying its
 *  purchase order's figures (legacy Requisition_ListView_Purchaseorder_ViewMode). */
interface HistoryRow {
  oid: string;
  poOid: string;
  poid: string | null;
  vendorName: string | null;
  numItems: number;
  totalPrice: string | null;
  shippingOptionName: string | null;
  orderDate: string | null;
  statusLabel: string | null;
  rqId: string | null;
  itemName: string | null;
}

/** Legacy POFilter windows over RequestedDate. Legacy defaults this from
 *  DefaultSetting.InventoryWorkFlow and falls back to 1M. */
// The presets now live in the shared ./IcmDateFilter, which every ICM form uses
// (standing rule 8) — this page had the only copy.

export default function PurchaseOrderListPage() {
  const navigate = useNavigate();
  const notify = useNotify();

  // Legacy opens on the vendor-grained MAIN view; "History" swaps the frame for
  // the ordered-lines view. One page, two views, exactly as the legacy action does.
  const [history, setHistory] = useState(false);
  const [dateFilter, setDateFilter] = useState<DateFilterId>(DEFAULT_DATE_FILTER);

  const [groups, setGroups] = useState<VendorGroupRow[]>([]);
  const [histRows, setHistRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** Row-selection ticks (owner request 2026-09-25). Legacy sets
   *  ShowSelectionColumn="False" on this view, so the column is an AI addition,
   *  not a port — it is here because the operator asked to be able to pick rows.
   *  One set for both views: the ids are different shapes (vendor Oid vs
   *  requisition Oid), so it is cleared whenever the view swaps. */
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  /** The legacy "Reports" dropdown, loaded from the lab's own Report Builder
   *  configuration for this view — not a hard-coded list, so a report the lab
   *  adds in ALPACA appears here with no code change. Today it returns exactly
   *  one: xr_ICMPOmail, captioned "PO". */
  const [reports, setReports] = useState<Array<{ reportName: string; caption: string }>>([]);
  const [reportsOpen, setReportsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get('/api/v1/purchase-orders/reports');
        if (!cancelled) setReports((res.data as Array<{ reportName: string; caption: string }>) ?? []);
      } catch {
        // Non-fatal: no dropdown rather than a broken screen.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      if (history) {
        const months = monthsOf(dateFilter);
        const res = await api.get('/api/v1/purchase-orders/history',
          { params: months == null ? {} : { months } });
        setHistRows((res.data ?? []) as HistoryRow[]);
      } else {
        const res = await api.get('/api/v1/purchase-orders/vendor-groups');
        setGroups((res.data ?? []) as VendorGroupRow[]);
      }
    } catch {
      setError(history ? 'Failed to load the ordering history.' : 'Failed to load items awaiting ordering.');
      if (history) setHistRows([]); else setGroups([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [history, dateFilter]);

  useEffect(() => { void load(); }, [load]);

  /** The TOP-BAR title follows the view, as legacy's does — History opens
   *  Requisition_ListView_Purchaseorder_ViewMode, captioned "Ordering Items
   *  History". Uses the shared `ai:topbar-title` override. */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title',
      { detail: history ? 'Ordering Items History' : null }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [history]);

  const groupsFiltered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return groups;
    return groups.filter(g => g.vendorName.toLowerCase().includes(t));
  }, [groups, search]);

  const histFiltered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return histRows;
    return histRows.filter(r =>
      (r.poid ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t) ||
      (r.rqId ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t)
    );
  }, [histRows, search]);

  /** MAIN view columns — the legacy three: Vendor | NumItem | TotalPrice
   *  (TotalPrice rendered with the model's own "{0:n2}" format).
   *  The header is "NumItem", SINGULAR: the property is NumItems but
   *  Requisition_ListView_Purchaseorder_Mainview overrides it with
   *  Caption="NumItem". It is the ONLY view that overrides this caption — the
   *  History grid below leaves it at the default, as legacy does. */
  const groupCols: AppColumn<VendorGroupRow>[] = [
    { field: 'vendorName', header: 'Vendor',     width: 320, text: g => g.vendorName },
    { field: 'numItem',    header: 'NumItem',    width: 110, type: 'number', text: g => String(g.numItem), value: g => g.numItem },
    { field: 'totalPrice', header: 'TotalPrice', width: 140, type: 'number', text: g => g.totalPrice.toFixed(2), value: g => g.totalPrice },
  ];

  /** HISTORY view columns — legacy's seven, in its order. */
  /** PO report, in a new tab as G5's Reports button opens it. The report is keyed
   *  by the PURCHASE ORDER, so the history row's poOid is all it needs. */
  async function openPoReport(poOid: string) {
    try {
      if (!(await openPoReportInNewTab(poOid))) notify.warning(PO_REPORT_TAB_BLOCKED);
    } catch {
      notify.error('Failed to generate the PO report.');
    }
  }

  /* NO "POReport" COLUMN — the GoLive History grid does not have one; the PO
     report is reached from the "Reports" dropdown instead (owner report
     2026-09-26). Legacy's POReport action is Category="RecordEdit", which XAF
     draws as an inline row command, and that column is not shown on this view.
     The report ITSELF is unchanged; only its entry point moved. */
  /** Run one entry of the Reports dropdown against the ticked row.
   *
   *  Legacy's report action requires a SINGLE selected object
   *  (SelectionDependencyType.RequireSingleObject) — the report is per purchase
   *  order — so the same rule applies here.
   *
   *  Only `xr_ICMPOmail` has an Alpaca Intelligence renderer today (it is the PO
   *  report Receiving Items already serves). Anything else the lab configures in
   *  ALPACA's Report Builder is listed but says so plainly rather than silently
   *  doing nothing — the DevExpress report engine those definitions need is not
   *  ported. */
  async function runReport(reportName: string, caption: string) {
    setReportsOpen(false);
    const ids = Array.from(selected) as string[];
    if (ids.length !== 1) { notify.warning('Select one purchase order to run a report.'); return; }
    const row = histRows.find(r => r.oid === ids[0]);
    if (!row) return;

    if (reportName === 'xr_ICMPOmail') { await openPoReport(row.poOid); return; }
    notify.warning(`"${caption}" is an ALPACA Report Builder report; it is not available in Alpaca Intelligence yet.`);
  }

  const histCols: AppColumn<HistoryRow>[] = [
    { field: 'poid',               header: 'PO#',             width: 130, text: r => r.poid ?? '—', render: r => r.poid ?? '—' },
    { field: 'vendorName',         header: 'Vendor',          width: 190, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'numItems',           header: 'NumItems',        width: 100, type: 'number', text: r => String(r.numItems), value: r => r.numItems },
    { field: 'totalPrice',         header: 'TotalPrice',      width: 120, type: 'number', text: r => r.totalPrice ?? '0', value: r => Number(r.totalPrice ?? 0) },
    { field: 'shippingOptionName', header: 'Shipping Option', width: 140, text: r => r.shippingOptionName ?? '—', render: r => r.shippingOptionName ?? '—' },
    { field: 'orderDate',          header: 'Order Date',      width: 120, text: r => fmtLocalDate(r.orderDate) ?? '—', render: r => fmtLocalDate(r.orderDate) ?? '—' },
    { field: 'statusLabel',        header: 'Status',          width: 180, type: 'status', text: r => r.statusLabel ?? '—' },
  ];

  /** Legacy Tar_CustomProcessSelectedItem: clicking a vendor row opens a NEW
   *  purchase order already carrying that vendor's whole pending basket. */
  function openVendor(g: VendorGroupRow) {
    if (!g.vendorId) {
      notify.warning('These requisitions have no vendor, so no purchase order can be raised for them. Set a vendor on each line in Item Requisition first.');
      return;
    }
    navigate(`/m/icm-purchase-order/new?vendorId=${g.vendorId}&vendor=${encodeURIComponent(g.vendorName)}`);
  }

  /** Ticked rows export on their own; with nothing ticked the whole list does —
   *  the ordinary grid convention, and what stops the new checkbox column from
   *  being decoration. */
  const histExport   = selected.size ? histFiltered.filter(r => selected.has(r.oid)) : histFiltered;
  const groupsExport = selected.size ? groupsFiltered.filter(g => selected.has(g.vendorId ?? '(none)')) : groupsFiltered;

  const exportData = history
    ? { filename: 'ordering-items-history', ...gridExportMatrix(histExport, histCols) }
    : { filename: 'ordering-items', ...gridExportMatrix(groupsExport, groupCols) };

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            {history ? (
              <>
                {/* The way BACK, now that History drops the "Ordering" button. */}
                <span onClick={() => { setHistory(false); setSelected(new Set()); }}
                      style={{ cursor: 'pointer' }}>Ordering Items</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">Ordering Items History</span>
              </>
            ) : (
              <span className="dp-bc-active">Ordering Items</span>
            )}
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-purchase-orders" auditFormName="Ordering Items" title={history ? 'Ordering Items History' : 'Ordering Items'} loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          {/* Legacy hides New / Edit / Delete on both views (AllowNew=False,
              AllowDelete=False, Edit in HiddenActions) — a PO is raised by
              picking a vendor row, never by a New button. */}
          <AppToolbar
            left={
              <>
                {/* The History toggle belongs to the MAIN view only — legacy's
                    POTracking targets Requisition_ListView_Purchaseorder_Mainview
                    alone, so the History view has no way back on its bar (the
                    breadcrumb is it). */}
                {!history && (
                  <button className="ca-btn"
                    onClick={() => { setHistory(true); setSelected(new Set()); }}
                    title="Show the requisitions already ordered">
                    <i className="ti ti-history" /> History
                  </button>
                )}
                {/* REPORTS — legacy's data-driven dropdown
                    (FormReportPermissionViewController), listing the custom
                    reports the lab configured against this view. */}
                {history && reports.length > 0 && (
                  <span className="po-reports-wrap">
                    <button className="ca-btn" onClick={() => setReportsOpen(o => !o)}
                      title="Run a report for the selected purchase order">
                      <i className="ti ti-file-text" /> Reports <i className="ti ti-chevron-down" />
                    </button>
                    {reportsOpen && (
                      <>
                        <div className="po-reports-backdrop" onClick={() => setReportsOpen(false)} />
                        <div className="po-reports-menu" role="menu">
                          {reports.map(r => (
                            <button key={r.reportName} type="button" role="menuitem"
                              className="po-reports-item"
                              onClick={() => void runReport(r.reportName, r.caption)}>
                              {r.caption}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </span>
                )}
                {history && <DateFilterSelect value={dateFilter} onChange={setDateFilter} />}
              </>
            }
            onRefresh={load}
            refreshing={loading}
            exportData={exportData}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={history ? 'Search PO#, vendor, RQID, item…' : 'Search vendor…'} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : history ? (
            <AppDataGrid<HistoryRow>
              rows={histFiltered}
              columns={histCols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              onRowClick={r => navigate(`/m/icm-purchase-order/${r.poOid}`)}
              emptyText="No requisitions have been ordered in this period."
            />
          ) : (
            <AppDataGrid<VendorGroupRow>
              rows={groupsFiltered}
              columns={groupCols}
              getRowId={g => g.vendorId ?? '(none)'}
              loading={loading}
              loadMs={loadMs}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              onRowClick={openVendor}
              emptyText="Nothing is waiting to be ordered."
            />
          )}
        </div>
      </div>
    </div>
  );
}
